import React, { useCallback, useEffect, useState } from 'react';
import { getOrgWelcomeBonus, updateOrgWelcomeBonus, getOrgWelcomeBonusWallets } from '../../services/api';
import './GuestRegistrationPanel.css';

const errorOf = (err, fallback) => err?.response?.data?.error || fallback;
const num = (v) => Number(v || 0).toLocaleString('ru-RU');

/* Приветственный бонус за подписку на бота: начисляется в iiko один раз на
   номер телефона — подписка во втором мессенджере его не повторяет.
   Оформление — общие классы .greg из панели регистрации. */
const WelcomeBonusPanel = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [wallets, setWallets] = useState(null);
  const [walletsError, setWalletsError] = useState('');
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const apply = (d) => {
    setData(d);
    setForm({
      welcome_bonus_enabled: d.settings.welcome_bonus_enabled,
      welcome_bonus_amount: d.settings.welcome_bonus_amount ?? '',
      welcome_bonus_wallet: d.settings.welcome_bonus_wallet || '',
    });
  };

  const load = useCallback(() => getOrgWelcomeBonus().then(apply), []);
  useEffect(() => { load(); }, [load]);

  const loadWallets = async () => {
    setWalletsError(''); setWalletsLoading(true);
    try {
      const res = await getOrgWelcomeBonusWallets();
      setWallets(res.wallets);
      if (res.error) setWalletsError(res.error);
    } catch (err) {
      setWalletsError(errorOf(err, 'Не удалось получить кошельки из iiko'));
    } finally {
      setWalletsLoading(false);
    }
  };

  if (!data || !form) return null;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMessage(null);
    try {
      apply(await updateOrgWelcomeBonus(form));
      setMessage({ ok: true, text: 'Сохранено' });
    } catch (err) {
      setMessage({ ok: false, text: errorOf(err, 'Не удалось сохранить') });
    } finally {
      setBusy(false);
    }
  };

  const saved = data.settings.welcome_bonus_wallet;
  const walletOptions = wallets || (saved ? [{ name: saved }] : []);
  const { stats } = data;

  return (
    <form className="panel ui-settings-panel ui-form greg" onSubmit={submit}>
      <h4>Приветственный бонус за подписку</h4>
      <p className="ui-settings-hint">
        Гость поделился номером в боте — iiko начисляет ему бонусы, а бот сообщает об этом. Один раз на номер
        телефона: подписка и в Telegram, и в MAX, отписка и повторная подписка второго бонуса не дают. Только
        Telegram и MAX — в VK номер не подтверждён. Гостю без карты в iiko бонус придёт после регистрации
        через бота или при первом «Мой профиль», когда карту заведут на кассе. Кто подписался до включения,
        бонус не получает.
      </p>

      {!data.cloud_connected && (
        <div className="ui-inline-error">Сначала подключите iiko Cloud API — без него бонус не начислить.</div>
      )}

      <label className={`greg-toggle ${form.welcome_bonus_enabled ? 'on' : ''}`}>
        <input type="checkbox" checked={form.welcome_bonus_enabled} onChange={set('welcome_bonus_enabled')}
               disabled={!data.cloud_connected} />
        <span>Начислять бонус за подписку</span>
      </label>

      <div className="ui-field">
        <label>Сколько бонусов</label>
        <input type="number" min="1" max="100000" step="1" placeholder="Например, 300"
               value={form.welcome_bonus_amount} onChange={set('welcome_bonus_amount')} />
      </div>

      <div className="ui-field">
        <label>Кошелёк iiko</label>
        <div className="greg-row">
          <select value={form.welcome_bonus_wallet} onChange={set('welcome_bonus_wallet')}>
            <option value="">Бонусы — как в «Мой профиль»</option>
            {walletOptions.map((w) => (
              <option key={w.name} value={w.name}>{w.name}{w.bonus ? ' (бонусный)' : ''}</option>
            ))}
          </select>
          {data.cloud_connected && (
            <button type="button" className="ui-btn-ghost" onClick={loadWallets} disabled={walletsLoading}>
              {walletsLoading ? 'Загружаем…' : 'Загрузить из iiko'}
            </button>
          )}
        </div>
        {walletsError && <div className="ui-hint-error">{walletsError}</div>}
      </div>

      <div className="ui-settings-hint">
        Текст сообщения гостю — в «Оформлении бота», поле «Приветственный бонус начислен».
      </div>

      <div className="greg-stats">
        {data.ready ? <span className="ui-status on">Бонус начисляется</span>
          : <span className="ui-status">Выключен</span>}
        <span className="greg-count">Начислено: <b>{num(stats.credited)}</b> гостям, всего <b>{num(stats.credited_sum)}</b></span>
        {stats.failed > 0 && <span className="greg-count">iiko отказал: <b>{stats.failed}</b></span>}
        {stats.unknown > 0 && (
          <span className="greg-count">Нет ответа iiko, проверьте вручную: <b>{stats.unknown}</b></span>
        )}
      </div>

      {message && <div className={message.ok ? 'ui-inline-success' : 'ui-inline-error'}>{message.text}</div>}
      <div className="ui-form-actions">
        <button type="submit" className="ui-btn-primary" disabled={busy}>Сохранить</button>
      </div>
    </form>
  );
};

export default WelcomeBonusPanel;
